import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Plus, Save, Play, X, Clock, Check, ChevronUp, ChevronDown, Maximize2, Minimize2, FileText } from 'lucide-react';
import { BASE_URL } from '../config/api';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentTest, setCurrentTest] = useState({ 
    title: '', 
    subject: '',
    questions: [] 
  });
  
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    explanation: '', // Add explanation field
    type: 'multiple',
    subject: '',
    options: ['', '', '', ''],
    correctAnswer: 0
  });

  const [expiryDateTimes, setExpiryDateTimes] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isQuestionBankVisible, setIsQuestionBankVisible] = useState(true);
  const [isTestCodesVisible, setIsTestCodesVisible] = useState(true);
  const [testCodes, setTestCodes] = useState([]);
  const [isExistingTestsVisible, setIsExistingTestsVisible] = useState(true);
  const [existingTests, setExistingTests] = useState([]);
  const [expandedTestId, setExpandedTestId] = useState(null);
  const [launchingTests, setLaunchingTests] = useState({});
  const [reportsData, setReportsData] = useState({});
  const [expandedReportCard, setExpandedReportCard] = useState(null);
  const [deliveryTypes, setDeliveryTypes] = useState({});
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType');
    return {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/subjects`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setSubjects(data);
        } else {
          console.error('Failed to fetch subjects');
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
      } finally {
        setIsLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedSubject) {
        setQuestions([]);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}/api/questions/subject?subject=${selectedSubject}`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setQuestions(data);
        } else {
          console.error('Failed to fetch questions');
          setQuestions([]);
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
        setQuestions([]);
      }
    };

    fetchQuestions();
  }, [selectedSubject]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  useEffect(() => {
    const fetchTestCodes = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/tests/testCodes`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setTestCodes(data);
        } else {
          console.error('Failed to fetch test codes');
        }
      } catch (error) {
        console.error('Error fetching test codes:', error);
      }
    };

    fetchTestCodes();
  }, []);

  const handleSubjectChange = (value) => {
    setSelectedSubject(value);
    setNewQuestion(prev => ({ ...prev, subject: value }));
    setCurrentTest(prev => ({ ...prev, subject: value }));
    
    // Clear existing tests when no subject is selected
    if (!value) {
      setExistingTests([]);
      return;
    }

    // Fetch tests for selected subject
    fetchTestsBySubject(value);
  };

  const fetchTestsBySubject = async (subjectId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/tests/subjectId?subjectId=${subjectId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExistingTests(data);
      } else {
        console.error('Failed to fetch tests');
        setExistingTests([]);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
      setExistingTests([]);
    }
  };

  const handleQuestionTypeChange = (type) => {
    setNewQuestion({
      ...newQuestion,
      type,
      options: type === 'multiple' ? ['', '', '', ''] : ['True', 'False'],
      correctAnswer: 0
    });
  };

  const handleAddQuestion = async () => {
    if (newQuestion.question.trim() !== '' && newQuestion.subject !== '') {
      try {
        const questionData = {
          text: newQuestion.question,
          explanation: newQuestion.explanation, // Add explanation to request body
          subject: {
            id: parseInt(newQuestion.subject)
          },
          optionList: newQuestion.options.map((text, index) => ({
            text,
            isCorrect: index === newQuestion.correctAnswer
          }))
        };

        const response = await fetch(`${BASE_URL}/api/questions`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(questionData)
        });

        if (response.ok) {
          // Reset everything including selected subject
          setNewQuestion({
            question: '',
            explanation: '', // Reset explanation too
            type: 'multiple',
            subject: '',
            options: ['', '', '', ''],
            correctAnswer: 0
          });
          setSelectedSubject('');
          setCurrentTest(prev => ({ ...prev, subject: '' }));
          setQuestions([]);
        } else {
          throw new Error('Failed to add question');
        }
      } catch (error) {
        console.error('Error:', error.message);
        alert(error.message + '. Please try again.');
      }
    }
  };

  const handleAddToTest = (question) => {
    setCurrentTest({
      ...currentTest,
      questions: [...currentTest.questions, question]
    });
    setSelectedQuestionIds(prev => new Set([...prev, question.id]));
  };

  const handleRemoveQuestion = (indexToRemove) => {
    const questionId = currentTest.questions[indexToRemove].id;
    setCurrentTest({
      ...currentTest,
      questions: currentTest.questions.filter((_, index) => index !== indexToRemove)
    });
    setSelectedQuestionIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(questionId);
      return newSet;
    });
  };

  const handleSaveTest = async () => {
    if (currentTest.title && currentTest.subject && currentTest.questions.length > 0) {
      try {
        const response = await fetch(`${BASE_URL}/api/tests`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            title: currentTest.title,
            subject: {
              id: parseInt(currentTest.subject)
            },
            questionList: currentTest.questions.map(q => ({
              id: q.id,
              text: q.question,
              subject: {
                id: parseInt(q.subject)
              },
              optionList: q.options.map((text, index) => ({
                text,
                isCorrect: index === q.correctAnswer
              }))
            }))
          })
        });

        if (response.ok) {
          // Reset the form
          setCurrentTest({ title: '', subject: '', questions: [] });
          setSelectedQuestionIds(new Set()); // Reset selected questions
          setToast({
            show: true,
            message: 'Test salvat cu succes!',
            type: 'success'
          });

          // Refresh the tests list after 1 second
          setTimeout(() => {
            fetchTestsBySubject(currentTest.subject);
          }, 1000);
        } else {
          console.error('Failed to save test');
          setToast({
            show: true,
            message: 'Salvarea testului a eșuat. Încercați din nou.',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error saving test:', error);
        setToast({
          show: true,
          message: 'Salvarea testului a eșuat. Încercați din nou.',
          type: 'error'
        });
      }
    }
  };

  const handleLaunchTest = async (test, expiryDateTime) => {
    if (!test || !expiryDateTime) return;
    setLaunchingTests(prev => ({ ...prev, [test.id]: true }));

    try {
      const launchData = {
        testId: test.id,
        expiryDateTime: expiryDateTime,
        deliveryType: deliveryTypes[test.id] || 'ON_COMPLETION' // Default to ON_COMPLETION if not set
      };

      const response = await fetch(`${BASE_URL}/api/tests/launch`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(launchData)
      });

      const data = await response.json();

      if (response.status === 201 && data && data.startCode) {
        // Show success message with copy button
        setToast({
          show: true,
          message: (
            <div className="flex items-center space-x-2">
              <span>Test "{data.testTitle}" Test lansat! Cod: {data.startCode}</span>
              <button
                onClick={() => handleCopyCode(data.startCode)}
                className="bg-green-500/20 hover:bg-green-500/30 rounded px-2 py-1 text-sm"
              >
                Copiază Codul
              </button>
            </div>
          ),
          type: 'success'
        });

        // Refresh test codes
        const refreshResponse = await fetch(`${BASE_URL}/api/tests/testCodes`, {
          headers: getAuthHeaders()
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setTestCodes(refreshData);
        }

        // Reset form for this specific test
        setExpiryDateTimes(prev => ({
          ...prev,
          [test.id]: ''
        }));
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Launch error:', error);
      setToast({
        show: true,
        message: 'Lansarea testului a eșuat. Încercați din nou.',
        type: 'error'
      });
    } finally {
      setLaunchingTests(prev => ({ ...prev, [test.id]: false }));
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setToast({
        show: true,
        message: 'Cod copiat în clipboard!',
        type: 'success'
      });
    }).catch(() => {
      setToast({
        show: true,
        message: 'Copierea codului a eșuat',
        type: 'error'
      });
    });
  };

  const handleToggleExpand = (testId) => {
    setExpandedTestId(expandedTestId === testId ? null : testId);
  };

  const handleViewReports = async (testCode) => {
    // If already expanded, collapse the card
    if (expandedReportCard === testCode) {
      setExpandedReportCard(null);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/reports/testCode/${testCode}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setReportsData(prev => ({
          ...prev,
          [testCode]: data
        }));
        setExpandedReportCard(testCode);
      } else {
        throw new Error('Failed to fetch reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setToast({
        show: true,
        message: 'Încărcarea rapoartelor a eșuat',
        type: 'error'
      });
    }
  };

  return (
    <div className="min-h-screen bg-indigo-500 text-white relative">
      <div className="fixed inset-0 bg-indigo-500 -z-10"></div>
      <div className="relative z-10">
        {/* Notification Toast */}
        {toast.show && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in-down">
            <div className={`${
              toast.type === 'success' 
                ? 'bg-green-400/20 border-green-500/50' 
                : 'bg-red-400/20 border-red-500/50'
            } backdrop-blur-lg border rounded-lg shadow-lg p-4 flex items-center space-x-3`}>
              {toast.type === 'success' ? (
                <Check className="h-5 w-5 text-green-400" />
              ) : (
                <X className="h-5 w-5 text-red-400" />
              )}
              <p className="text-white font-medium">{toast.message}</p>
            </div>
          </div>
        )}

        <header className="container mx-auto py-6 px-4 flex justify-between items-center">
          <div 
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <Brain className="h-8 w-8" />
            <span className="text-2xl font-bold">PMind Panou Profesor</span>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Question Creation */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <h2 className="text-2xl font-bold mb-6">Creare Întrebare</h2>
              <div className="space-y-4">
                {/* Subject Selection */}
                <select
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-50"
                  value={selectedSubject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  disabled={isLoadingSubjects}
                >
                  <option value="">Selectează Materia</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>

                {/* Question Type Selection */}
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleQuestionTypeChange('multiple')}
                    className={`flex-1 py-2 px-4 rounded-lg ${
                      newQuestion.type === 'multiple' 
                        ? 'bg-yellow-400 text-purple-900' 
                        : 'bg-white/5'
                    }`}
                  >
                    Alegere Multiplă
                  </button>
                  <button
                    onClick={() => handleQuestionTypeChange('boolean')}
                    className={`flex-1 py-2 px-4 rounded-lg ${
                      newQuestion.type === 'boolean' 
                        ? 'bg-yellow-400 text-purple-900' 
                        : 'bg-white/5'
                    }`}
                  >
                    Adevărat/Fals
                  </button>
                </div>

                <textarea
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Introduceți întrebarea"
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})}
                  rows="3"
                />

                <textarea
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Introduceți explicația pentru răspunsul corect"
                  value={newQuestion.explanation}
                  onChange={(e) => setNewQuestion({...newQuestion, explanation: e.target.value})}
                  rows="2"
                />

                {/* Dynamic Options */}
                {newQuestion.options.map((option, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      className="flex-grow p-2 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder={`Opțiunea ${index + 1}`}
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...newQuestion.options];
                        newOptions[index] = e.target.value;
                        setNewQuestion({...newQuestion, options: newOptions});
                      }}
                      disabled={newQuestion.type === 'boolean'}
                    />
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={newQuestion.correctAnswer === index}
                      onChange={() => setNewQuestion({...newQuestion, correctAnswer: index})}
                      className="ml-2"
                    />
                  </div>
                ))}
                
                <button
                  onClick={handleAddQuestion}
                  disabled={!newQuestion.subject || !newQuestion.question.trim()}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-5 w-5 mr-2" /> Adaugă Întrebare
                </button>
              </div>
            </div>

            {/* Test Management Split into Two Sections */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <div className="space-y-6">
                {/* Create New Test Section */}
                <div className="bg-white/5 p-4 rounded-lg">
                  <h3 className="text-xl font-bold mb-4">Creează Test Nou</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      className="w-full p-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Titlul Testului"
                      value={currentTest.title}
                      onChange={(e) => setCurrentTest({...currentTest, title: e.target.value})}
                    />
                    
                    <select
                      className="w-full p-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-50"
                      value={selectedSubject}
                      onChange={(e) => handleSubjectChange(e.target.value)}
                      disabled={isLoadingSubjects}
                    >
                      <option value="">Selectează Materia</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>

                    <div className="space-y-2">
                      <p className="text-sm text-white/70">Întrebări Curente:</p>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {currentTest.questions.map((q, index) => (
                          <div key={index} className="bg-white/5 p-2 rounded-lg flex justify-between items-center">
                            <p className="text-sm truncate">{q.question}</p>
                            <button
                              onClick={() => handleRemoveQuestion(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleSaveTest}
                      disabled={!currentTest.title || !currentTest.subject || currentTest.questions.length === 0}
                      className="w-full bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="h-5 w-5 mr-2" /> Salvează Test
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question Bank */}
          <div className="mt-8 bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Bancă de Întrebări</h2>
              <button
                onClick={() => setIsQuestionBankVisible(!isQuestionBankVisible)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isQuestionBankVisible ? (
                  <ChevronUp className="h-6 w-6 text-white/70" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-white/70" />
                )}
              </button>
            </div>
            
            <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-500 ${
              isQuestionBankVisible ? 'opacity-100 mb-4' : 'opacity-0 h-0 overflow-hidden m-0'
            }`}>
              {questions.map((q) => (
                <div key={q.id} className={`bg-white/5 p-4 rounded-lg ${
                  selectedQuestionIds.has(q.id) ? 'border-2 border-yellow-400/50' : ''
                }`}>
                  <div className="text-yellow-300 text-sm mb-2">{q.subject.name}</div>
                  <p className="font-medium mb-2">{q.text}</p>
                  <p className="text-sm text-white/70 italic mb-4">{q.explanation}</p>
                  <div className="space-y-2 mb-4">
                    {q.optionList.map((opt) => (
                      <div 
                        key={opt.id} 
                        className={`text-sm p-2 rounded ${
                          opt.isCorrect 
                            ? 'bg-green-500/20 border border-green-500/30' 
                            : 'bg-white/5 border border-white/10'
                        }`}
                      >
                        {opt.text}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-white/70">
                      Tip: {q.optionList.length > 2 ? 'Alegere Multiplă' : 'Adevărat/Fals'}
                    </div>
                    <button
                      onClick={() => handleAddToTest({
                        id: q.id,
                        question: q.text,
                        type: q.optionList.length > 2 ? 'multiple' : 'boolean',
                        subject: q.subject.id,
                        options: q.optionList.map(opt => opt.text),
                        correctAnswer: q.optionList.findIndex(opt => opt.isCorrect)
                      })}
                      disabled={selectedQuestionIds.has(q.id)}
                      className={`${
                        selectedQuestionIds.has(q.id)
                          ? 'bg-green-500/20 text-green-300 cursor-not-allowed'
                          : 'bg-yellow-400 hover:bg-yellow-300 text-purple-900'
                      } text-sm py-1 px-3 rounded-full transition-colors`}
                    >
                      {selectedQuestionIds.has(q.id) ? 'Adăugată' : 'Adaugă la Test'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Existing Tests */}
          <div className="mt-8 bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Teste Existente</h2>
              <button
                onClick={() => setIsExistingTestsVisible(!isExistingTestsVisible)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isExistingTestsVisible ? (
                  <ChevronUp className="h-6 w-6 text-white/70" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-white/70" />
                )}
              </button>
            </div>
            
            <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-500 ${
              isExistingTestsVisible ? 'opacity-100 mb-4' : 'opacity-0 h-0 overflow-hidden m-0'
            }`}>
              {existingTests.map((test) => (
                <div key={test.id} className={`bg-white/5 p-4 rounded-lg ${
                  expandedTestId === test.id ? 'col-span-full' : ''
                }`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-yellow-300 text-sm mb-2">{test.subject.name}</div>
                      <p className="font-medium text-lg">{test.title}</p>
                    </div>
                    <button
                      onClick={() => handleToggleExpand(test.id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {expandedTestId === test.id ? (
                        <Minimize2 className="h-5 w-5 text-white/70" />
                      ) : (
                        <Maximize2 className="h-5 w-5 text-white/70" />
                      )}
                    </button>
                  </div>

                  {expandedTestId === test.id ? (
                    <div className="mb-6">
                      <div className="bg-white/5 rounded-lg p-4 mb-4">
                        <h4 className="font-medium mb-3">Întrebări:</h4>
                        <div className="space-y-4">
                          {test.questionList.map((q, qIndex) => (
                            <div key={q.id} className="bg-white/5 p-3 rounded-lg">
                              <p className="font-medium mb-2">{qIndex + 1}. {q.text}</p>
                              <div className="ml-4 space-y-2">
                                {q.optionList.map((opt) => (
                                  <div 
                                    key={opt.id}
                                    className={`text-sm p-2 rounded ${
                                      opt.isCorrect 
                                        ? 'bg-green-500/20 border border-green-500/30' 
                                        : 'bg-white/5 border border-white/10'
                                    }`}
                                  >
                                    {opt.text}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-white/70 mb-4">
                      Întrebări: {test.questionList.length}
                    </div>
                  )}

                  <div className="flex space-x-2 mb-3">
                    <button
                      onClick={() => setDeliveryTypes(prev => ({
                        ...prev,
                        [test.id]: 'ON_COMPLETION'
                      }))}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm ${
                        deliveryTypes[test.id] === 'ON_COMPLETION' || !deliveryTypes[test.id]
                          ? 'bg-yellow-400 text-purple-900'
                          : 'bg-white/5 text-white'
                      }`}
                    >
                      Arată Rezultate la Finalizare
                    </button>
                    <button
                      onClick={() => setDeliveryTypes(prev => ({
                        ...prev,
                        [test.id]: 'ON_EXPIRY'
                      }))}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm ${
                        deliveryTypes[test.id] === 'ON_EXPIRY'
                          ? 'bg-yellow-400 text-purple-900'
                          : 'bg-white/5 text-white'
                      }`}
                    >
                      Arată Rezultate la Expirare
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex space-x-2 items-center">
                      <Clock className="h-5 w-5 text-yellow-400" />
                      <input
                        type="datetime-local"
                        className="flex-1 p-2 rounded-lg bg-white/5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={expiryDateTimes[test.id] || ''}
                        onChange={(e) => setExpiryDateTimes(prev => ({
                          ...prev,
                          [test.id]: e.target.value
                        }))}
                      />
                    </div>
                    <button
                      onClick={() => handleLaunchTest(test, expiryDateTimes[test.id])}
                      disabled={!expiryDateTimes[test.id] || launchingTests[test.id]}
                      className="w-full bg-green-500 hover:bg-green-400 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {launchingTests[test.id] ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Se lansează...
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5 mr-2" /> Lansează Test
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Test Codes Section */}
          <div className="mt-8 bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Coduri de Test</h2>
              <button
                onClick={() => setIsTestCodesVisible(!isTestCodesVisible)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isTestCodesVisible ? (
                  <ChevronUp className="h-6 w-6 text-white/70" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-white/70" />
                )}
              </button>
            </div>
            
            <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-500 ${
              isTestCodesVisible ? 'opacity-100 mb-4' : 'opacity-0 h-0 overflow-hidden m-0'
            }`}>
              {testCodes.map((code) => {
                const isExpired = new Date(code.expiryDateTime) < new Date();
                const isExpanded = expandedReportCard === code.startCode;
                
                return (
                  <div 
                    key={code.id} 
                    className={`bg-white/5 p-4 rounded-lg ${
                      isExpired ? 'bg-red-500/10 border border-red-500/30' : ''
                    } ${isExpanded ? 'col-span-full' : ''}`}
                  >
                    <div className={`text-2xl font-bold mb-3 text-center ${
                      isExpired ? 'text-red-300' : 'text-yellow-300'
                    }`}>
                      {code.startCode}
                    </div>
                    <div className="mb-4">
                      <p className="font-medium text-lg mb-2">{code.testTitle}</p>
                      <div className="space-y-1">
                        <p className={`text-sm ${
                          isExpired ? 'text-red-300' : 'text-white/70'
                        }`}>
                          {isExpired ? 'Expirat: ' : 'Expiră: '}
                          {new Date(code.expiryDateTime).toLocaleString()}
                        </p>
                        <p className="text-sm text-white/70">
                          Rezultate: {code.reportDeliveryType === 'ON_COMPLETION' ? 'Afișate la finalizare' : 'Afișate la expirare'}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <button
                        onClick={() => handleViewReports(code.startCode)}
                        className="bg-purple-500 hover:bg-purple-400 text-white text-sm py-1 px-3 rounded-full flex items-center space-x-1"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        <span>{expandedReportCard === code.startCode ? 'Închide Rapoarte' : 'Vezi Rapoarte'}</span>
                      </button>
                      <button
                        onClick={() => handleCopyCode(code.startCode)}
                        className={`${
                          isExpired 
                            ? 'bg-red-400 hover:bg-red-300' 
                            : 'bg-yellow-400 hover:bg-yellow-300'
                        } text-purple-900 text-sm py-1 px-3 rounded-full flex items-center space-x-1`}
                      >
                        <span>Copiază Cod</span>
                      </button>
                    </div>

                    {/* Reports Section */}
                    {isExpanded && reportsData[code.startCode] && (
                      <div className="mt-6 bg-white/5 rounded-lg p-4">
                        <h4 className="text-lg font-medium mb-4">Rapoarte Test</h4>
                        <div className="space-y-4">
                          {reportsData[code.startCode].map((report) => (
                            <div key={report.studentEmail} className="bg-white/5 p-3 rounded-lg">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="font-medium">{report.studentName}</p>
                                  <p className="text-sm text-white/70">{report.studentEmail}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium">Punctaj</p>
                                  <p className="text-yellow-300">
                                    {report.totalCorrectAnswers}/{report.totalQuestions}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
